import styles from './NavBar.scss';

import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import debounce from 'lodash/debounce';
import React from 'react';

import { Navigation } from 'src/api/contentful/contentTypes.cloverdotcom';
import { IntercomWrapper } from 'src/services/intercom';
import { componentKey, getField } from 'src/utils/EntryUtil';

import { readCookie } from 'src/utils/Cookies';
import { QA_TEST_PREFIX } from '../NavTitle/NavTitle';
import { ReferralBanner } from '../ReferralBanner/ReferralBanner';
import { withBrowser } from '../Responsive/WithBrowser';
import { getReferralType } from '../SalesChannel/Util';
import { Sidebar } from '../Sidebar/Sidebar';
import { withSidebarContext } from '../SidebarContext/WithSidebarContext';
import { MarkLink } from './MarkLink/MarkLink';
import { ScrollDirection, computeScrollDirection } from './NavBarUtil';
import { NavItem } from './NavItem/NavItem';
import { UtilMenu } from './UtilMenu/UtilMenu';

// Accessibility Note: Submenus should not open when using the tab key to navigate through the menu,
// as keyboard users would then have to step through all submenu items to get to the next top-level item
// @See https://www.w3.org/WAI/tutorials/menus/flyout/

export const INITIAL_UI_STATE: Readonly<UiState> = {
  openIndex: -1,
  activeIndex: -1,
};

export interface INavBar extends React.AllHTMLAttributes<HTMLDivElement> {
  entry?: ReferencedEntry<Navigation>;
  sidebarEntry?: ReferencedEntry<Navigation>;
  utilEntry?: ReferencedEntry<Navigation>;
  mainContentRef?: React.RefObject<HTMLDivElement>;
  mobile?: boolean;
  sidebarOpen?: boolean;
  desktopOrLarger?: boolean;
  tablet?: boolean;
  children?: never;
  isSidebarLoading?: boolean;
}

type UiState = {
  // the index of the drawer that is open
  openIndex: number;

  // the index of the menu item that is "active" (hover or has focus)
  activeIndex: number;

  // window scroll direction
  scrollDirection?: ScrollDirection;
};

class NavBar extends React.PureComponent<INavBar, UiState> {

  wrapperGroupRef = React.createRef<HTMLDivElement>();
  resizeObserver: ResizeObserver | undefined;
  hasResizeObserver: boolean;

  get isDrawerOpen() {
    return this.state.openIndex > -1 && this.state.openIndex === this.state.activeIndex;
  }

  get shouldShowHeaderMenu() {
    return getField(this.props.entry, 'items', []).length > 0;
  }

  get isUtilMenuHidden() {
    return this.shouldShowHeaderMenu && this.state.scrollDirection === ScrollDirection.DOWN;
  }

  private lastScrollPosition = window.pageYOffset || 0;

  readonly state: UiState = { ...INITIAL_UI_STATE };

  readonly deactivateAll = debounce(() => this.setState({ ...INITIAL_UI_STATE }), 300);

  readonly closeAllDrawers = debounce((indexOrEvent?: number | React.MouseEvent<HTMLElement>, deactivate?: boolean) =>
    this.setState({ openIndex: -1, activeIndex: deactivate ? -1 : this.state.activeIndex }), 300);

  readonly onWindowScroll = debounce(() => {
    const { pageYOffset, innerHeight: viewportHeight } = window;

    const { scrollHeight: bodyHeight } = document.body;

    const scrollDirection = computeScrollDirection(
      this.lastScrollPosition,
      pageYOffset,
      // max scroll position
      Math.max(bodyHeight - viewportHeight, 0)
    );

    if (scrollDirection === ScrollDirection.NONE) return;

    this.setState({ scrollDirection }, () => this.lastScrollPosition = pageYOffset);
  });

  readonly cancelDeactivateAll = () => this.deactivateAll.cancel();

  componentDidMount() {
    window.addEventListener('keyup', this.onWindowKeyUp);
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });

    this.hasResizeObserver = 'ResizeObserver' in window;
    if (!this.hasResizeObserver || !this.wrapperGroupRef) return;
    const update = this.updateSize;
    this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        update(entry.contentRect);
      }
    });

    this.resizeObserver.observe(this.wrapperGroupRef.current as Element);
  }

  componentWillUnmount() {
    window.removeEventListener('keyup', this.onWindowKeyUp);
    window.removeEventListener('scroll', this.onWindowScroll);
    if (this.hasResizeObserver && this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  componentDidUpdate() {
    this.setRootScroll(!this.isDrawerOpen);
    this.setMainContentAria(this.isDrawerOpen);
    this.toggleIntercomLauncher();
  }

  render() {
    const { openIndex, activeIndex } = this.state;

    const {
      entry,
      sidebarEntry,
      utilEntry,
      mobile,
      sidebarOpen,
      desktopOrLarger,
      tablet,
      mainContentRef,
      className,
      children,
      isSidebarLoading,
      ...props
    } = this.props;

    const items = getField(entry, 'items', []);

    const sidebar = <Sidebar entry={sidebarEntry} forceClose={this.isDrawerOpen} onFocus={this.deactivateAll} />;

    return (
      <header className={cx(styles.fixedWrapper)}>
        {(getReferralType() === 'BC') && <ReferralBanner mainContentRef={mainContentRef} wrapperGroupRef={this.wrapperGroupRef} scrollDirection={this.state.scrollDirection} />}
        <div ref={this.wrapperGroupRef} className={cx(styles.wrapperGroup, { [styles.wrapperGroupHideUtil]: (this.isUtilMenuHidden && !(readCookie('referralType') === 'BC')) })}>
          <UtilMenu entry={utilEntry} hidden={this.isUtilMenuHidden} mobile={mobile} />
          <div
            data-test={QA_TEST_PREFIX}
            className={cx(className, styles.header, { [styles.headerOpenDrawer]: this.isDrawerOpen })}
            {...props}
            aria-modal={this.isDrawerOpen ? true : undefined}
          >
            <div className={styles.group}>
              {this.shouldShowHeaderMenu && (
                <nav className={styles.nav}>
                  <MarkLink hidden={!this.isUtilMenuHidden} />
                  {items.length > 0 && (
                    <ul
                      className={styles.navbar}
                    >
                      {items.map((item, index) => (
                        <NavItem
                          desktop={desktopOrLarger}
                          tablet={tablet}
                          mobile={mobile}
                          entry={item}
                          index={index}
                          key={componentKey(item, index)}
                          open={index === openIndex && index === activeIndex}
                          active={index === activeIndex}
                          onCloseDrawer={this.closeAllDrawers}
                          onOpenDrawer={this.openDrawer}
                          onActivate={this.activate}
                        />
                      ))}
                    </ul>
                  )}
                </nav>
              )}
            </div>
            {(!mobile && !isSidebarLoading) && sidebar}
          </div>
        </div>
        {(mobile && !isSidebarLoading) && sidebar}
      </header>
    );
  }

  updateSize = (nextSize: DOMRect) => {
    if (this.props.mainContentRef?.current) this.props.mainContentRef.current.style.paddingTop = `${nextSize.height}px`;
    if (this.wrapperGroupRef.current) this.wrapperGroupRef.current.style.setProperty('--header-height', `${nextSize.height}px`);
  };

  readonly onWindowKeyUp = (e: KeyboardEvent) => {
    if (e.defaultPrevented) return;
    switch (e.key) {
      case 'Escape':
      case 'Esc':
        this.closeAllDrawers();
        return;
      default:
        return;
    }
  };

  readonly activate = (activeIndex: number) => this.setState({ activeIndex });

  readonly openDrawer = (openIndex: number) => this.setState({ openIndex, activeIndex: openIndex });

  toggleIntercomLauncher() {
    const { mobile, sidebarOpen } = this.props;

    if (!mobile) return;

    if (this.isDrawerOpen || sidebarOpen) {
      IntercomWrapper.hideLauncher();
    } else {
      IntercomWrapper.showLauncher();
    }
  }

  setRootScroll(enable = false) {
    const rootEls = [document.documentElement, document.body];
    if (enable) {
      rootEls.forEach(el => el && el.classList.remove(styles.rootNoScroll));
    } else {
      rootEls.forEach(el => el && el.classList.add(styles.rootNoScroll));
    }
  }

  setMainContentAria(hidden = false) {
    const mainContentEl = this.props.mainContentRef && this.props.mainContentRef.current;
    if (!mainContentEl) return;
    if (hidden) {
      mainContentEl.setAttribute('aria-hidden', `${true}`);
    } else {
      mainContentEl.removeAttribute('aria-hidden');
    }
  }
}

const Browsered = withBrowser(browser => ({
  mobile: browser.is.mobile,
  tablet: browser.is.tablet,
  desktopOrLarger: browser.greaterThan.tablet,
}))(NavBar);

const SidebarContexted = withSidebarContext(context => ({
  sidebarOpen: context.isOpen,
  isSidebarLoading: context.isLoading,
}))(Browsered);

export {
  SidebarContexted as NavBar,
  NavBar as _NavBar
};
